import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { on } from '@ember/modifier'
import { type IntlService, t } from 'ember-intl'
import { optional } from 'teacher-ui-client/helpers/optional'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import {
  FormWrapperSubmitButton,
  type ExternalSubmitState,
} from 'teacher-ui-client/components/form-wrapper/submit-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { didCancel, task, timeout } from 'ember-concurrency'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type Owner from '@ember/owner'

interface Args {
  // intentionally optional - the class record can get deleted
  schoolClass?: SchoolClass
  onSubmit: (schoolClass: SchoolClass) => unknown
  onClose?: () => unknown
}

export class DeleteClassConfirmationForm extends Component<Args> {
  @service resourceConflict!: ResourceConflictService

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service log!: Log

  @tracked submitState: ExternalSubmitState = 'default'

  @tracked schoolClassName = ''

  @tracked teacherCount = 0

  @tracked studentCount = 0

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    // Set in the constructor so that it persists while the form is open, even if the class has been deleted
    if (this.args.schoolClass) {
      this.schoolClassName = this.args.schoolClass.name
      this.teacherCount = this.args.schoolClass.teachers.length
      this.studentCount = this.args.schoolClass.students.length
    }
  }

  submitAction = task(async (schoolClass) => {
    await this.args.onSubmit(schoolClass)
  })

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    this.submitState = 'load'

    try {
      await this.submitAction.perform(this.args.schoolClass)
      this.submitState = 'done'
      await timeout(1000)
      await this.args.onClose?.()
      this.flashQueue.addSuccess({
        subtitle: this.intl.t('manage.manageClasses.classes.deleteClass.success', { name: this.schoolClassName }),
      })
    } catch (error: any) {
      this.submitState = 'error'
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('delete class', error)
      } else if (!didCancel(error)) {
        this.flashQueue.addFail({
          subtitle: this.intl.t('manage.manageClasses.classes.deleteClass.error', { name: this.schoolClassName }),
        })
        this.log.error('failed to delete class', error ?? '')
      }
    }
  }

  <template>
    <form {{on "submit" this.onSubmit}}>
      <div class="p-4">
        <p class="mb-3">
          {{t
            "manage.manageClasses.classes.deleteClass.confirmation"
            className=this.schoolClassName
            teacherCount=(t "manage.manageClasses.classes.deleteClass.teacherCount" count=this.teacherCount)
            studentCount=(t "manage.manageClasses.classes.deleteClass.studentCount" count=this.studentCount)
            htmlSafe=true
          }}
        </p>
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onClose)}}>
          {{t "manage.manageClasses.classes.deleteClass.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-submit
          @defaultText={{t "manage.manageClasses.classes.deleteClass.ok"}}
          @state={{this.submitState}}
          @appearance={{ButtonAppearance.regular}}
        />
      </div>
    </form>
  </template>
}

export default DeleteClassConfirmationForm
